/**
 * Modern Registration page with dark mode support
 */

import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useRegister } from '../../hooks/useAuth';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';
import { strongPasswordSchema } from './password-policy';

const registerSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email invalido'),
  password: strongPasswordSchema,
  confirmPassword: z.string(),
  website: z.string().max(200).optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas nao conferem',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  // SEO meta tags for registration page
  useSEO(SEO_CONFIGS.register);

  const registerMutation = useRegister();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = (data: RegisterForm) => {
    registerMutation.mutate({
      name: data.name,
      email: data.email,
      password: data.password,
      website: data.website,
    });
  };

  return (
    <div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Criar Conta</h1>
        <p className="text-neutral-600 dark:text-neutral-400 mt-2">Junte-se ao maior bolão da Copa 2026!</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div
          aria-hidden="true"
          className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden"
        >
          <input
            {...register('website')}
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <Input
          {...register('name')}
          type="text"
          placeholder="Seu nome"
          icon={<User className="w-5 h-5" />}
          error={errors.name?.message}
          autoComplete="name"
        />

        <Input
          {...register('email')}
          type="email"
          placeholder="Seu email"
          icon={<Mail className="w-5 h-5" />}
          error={errors.email?.message}
          autoComplete="email"
        />

        <Input
          {...register('password')}
          type="password"
          placeholder="Sua senha"
          icon={<Lock className="w-5 h-5" />}
          error={errors.password?.message}
          autoComplete="new-password"
          showPasswordToggle
        />

        <Input
          {...register('confirmPassword')}
          type="password"
          placeholder="Confirme sua senha"
          icon={<Lock className="w-5 h-5" />}
          error={errors.confirmPassword?.message}
          autoComplete="new-password"
          showPasswordToggle
        />

        <Button
          type="submit"
          className="w-full"
          loading={registerMutation.isPending}
        >
          Criar Conta
        </Button>
      </form>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-neutral-200 dark:border-neutral-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">
            ou
          </span>
        </div>
      </div>

      <p className="text-center text-neutral-600 dark:text-neutral-400">
        Ja tem uma conta?{' '}
        <Link
          to="/entrar"
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          Entrar
        </Link>
      </p>
    </div>
  );
}
